import useGetResults from '../hooks/useGetResults'
import { ResultsDetail } from '../components/ResultsPopup'
import { Panel } from "@ui5/webcomponents-react";
import { useEffect, useState, useMemo } from 'react'
import {Link, useNavigate} from 'react-router-dom'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tooltip from '@mui/material/Tooltip';
import { useTableStyles } from '../utils/tableStyles'
import { useTheme } from '../utils/ThemeContext'
import '../styles/MaterialTableStyles.css';

const RESULTS_PAGE_SIZE_KEY = 'results_pageSize';
const RESULTS_EXPANDED_KEY = 'results_expanded';

const Results = () => {
  const { theme } = useTheme();
  const { panelBackgroundColor, muiTableBodyCellSx, muiTableHeadCellSx, muiTablePaperSx, muiTopToolbarSx, muiBottomToolbarSx } = useTableStyles();
  const navigate = useNavigate();
  //results from backend.  useState in order to share the data across functions/components
  const [resultData, updateResultData] = useState([])
  const [loading, setLoading] = useState(true)
  const [showScrollHint, setShowScrollHint] = useState(true)
  const isMobile = useMediaQuery('(max-width:768px)')
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(RESULTS_EXPANDED_KEY) || '{}'); }
    catch { return {}; }
  })
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: parseInt(sessionStorage.getItem(RESULTS_PAGE_SIZE_KEY) || '10', 10),
  })

  const handleTableScroll = (e) => {
    const el = e.currentTarget;
    setShowScrollHint(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };
   
  //this goes to the backend and gets the weekly results
  const {
    data: rootData,
   // isLoading: isRootLoading,
   // isError: isRootError,
  } = useGetResults()
  
  //update the resultData state object with the JSON response fro the backend
  const createResultTileDetails = () => {
    if (rootData) {
      updateResultData(rootData.weeklyresults)
      setLoading(false)
    } else {
      updateResultData([]);
    }
  }
  
  //this gets called every time rootData is updated
  useEffect(() => {
    if (rootData) {
      createResultTileDetails()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootData])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(
    () => [
              {
                header: 'Week',
                accessorKey: 'datePlayed',
                id: 'datePlayed',
                size: 90,
                Cell: ({renderedCellValue, row}) => {
                  return(
                    <>
                      <Tooltip title="View weekly scores" placement="right" arrow>
                        <Link style={{textDecoration: "none", color: theme.primary, fontWeight: 'bold'}} to={{pathname: '/weeklyscoreboard', search: `?datePlayed=${row.original.datePlayed}`}}>
                            {renderedCellValue}
                        </Link>
                      </Tooltip>
                    </>
                  )
                }
              },
              {
                header: 'Course',
                accessorKey: 'course',
                size: 100,
              },
              {
                header: 'Low Net',
                accessorKey: 'netWinnerPlayer',
                size: 100,
                Cell: ({renderedCellValue}) => (
                  <span style={{ fontWeight: 'bold', color: theme.primary }}>{renderedCellValue}</span>
                )
              },
              {
                header: 'Low Gross',
                accessorKey: 'grossWinnerPlayer',
                size: 110,
                Cell: ({renderedCellValue}) => (
                  <span style={{ fontWeight: 'bold', color: theme.primary }}>{renderedCellValue}</span>
                )
              },
            ],
    );

    const table = useMaterialReactTable({
      columns: columns,
      data: resultData,
      enableColumnPinning: false,
      enableHiding: false,
      enableColumnFilters: false,
      enableColumnActions: false,
      enableStickyHeader: true,
      enableTopToolbar: false,
      enableExpanding: true,
      renderDetailPanel: ({ row }) => {
        const data = row.original;
        return (
          <div style={{ width: isMobile ? '100%' : '400px' }} onClick={(e) => e.stopPropagation()}>
            <ResultsDetail
              datePlayed={data.datePlayed}
              course={data.course}
              numPlayers={data.numPlayers}
              netWinner={data.netWinnerPlayer}
              grossWinner={data.grossWinnerPlayer}
              netWinnerNet={data.netWinnerNetScore}
              netWinnerGross={data.netWinnerGrossScore}
              grossWinnerGross={data.grossWinnerGrossScore}
              twoDollarSkins={data.twodollarskins}
              fiveDollarSkins={data.fivedollarskins}
              closestToPin={data.closestToPin}
            />
          </div>
        );
      },
      initialState: { density: 'compact',
                      columnPinning: { left: ['datePlayed'] },
                      sorting: [
                        {
                          id: 'datePlayed',
                          desc: true,
                        },
                      ],
                    },
      layoutMode: 'grid',
      state: { isLoading: loading, pagination, expanded },
      onExpandedChange: (updater) => {
        setExpanded((prev) => {
          const next = typeof updater === 'function' ? updater(prev) : updater;
          sessionStorage.setItem(RESULTS_EXPANDED_KEY, JSON.stringify(next));
          return next;
        });
      },
      onPaginationChange: (updater) => {
        setPagination((prev) => {
          const next = typeof updater === 'function' ? updater(prev) : updater;
          sessionStorage.setItem(RESULTS_PAGE_SIZE_KEY, String(next.pageSize));
          return next;
        });
      },
      muiTableContainerProps: { onScroll: handleTableScroll },
      muiTableBodyCellProps: {
        sx: muiTableBodyCellSx,
      },
      muiTableHeadCellProps: {
        sx: muiTableHeadCellSx,
      },
      muiTablePaperProps: { sx: muiTablePaperSx },
      muiTopToolbarProps: { sx: muiTopToolbarSx },
      muiBottomToolbarProps: { sx: muiBottomToolbarSx },
      muiTableBodyRowProps: ({ row }) => ({
        onClick: () => navigate({ pathname: '/weeklyscoreboard', search: `?datePlayed=${row.original.datePlayed}` }),
        sx: { cursor: 'pointer' },
      }),
      muiExpandButtonProps: () => ({
        onClick: (e) => e.stopPropagation(),
      }),
    });

  return(
    <Panel fixed stickyHeader style={{backgroundColor: panelBackgroundColor}} headerText={"Weekly Results"}>
      <Container>
        <div style={{ position: 'relative' }}>
          <MaterialReactTable table={table} />
          {isMobile && showScrollHint && (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: '48px',
              background: `linear-gradient(to right, transparent, ${theme.panelBgFade})`,
              pointerEvents: 'none',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{ fontSize: '1.2rem', color: theme.primary, opacity: 0.8 }}>›</span>
            </div>
          )}
        </div>
      </Container>
    </Panel>
    )
  }
  
  export default Results; 